/** Every canvas this space's activities declare, and the addresses and pairings that reach one. */

import { useState } from "react";

import { Button, CopyField, Field, Input, Stack, StatusBadge, Toolbar } from "@/components/ui";

import { stamp } from "../../account/lib/present";
import {
  useApproveCanvasPairing,
  useCanvasPairings,
  useUnpairCanvas,
  type CanvasPairing,
} from "../../account/lib/queries";
import { CanvasKindChip, Chip } from "../../components/Chips";
import { Muted } from "../../components/Text";
import { canvasAddresses } from "../../lib/canvasAddress";
import { canvasesOf } from "../../lib/catalog";
import type { SpaceActivities } from "../../spaces/lib/queries";
import type { NamedCanvas, SpaceRow } from "../../types";

import { Loaded, Nothing, Section } from "./Section";
import type { PanelId } from "./panels";

/** One canvas kind, merged across every added activity that declares it. */
interface CanvasRow extends NamedCanvas {
  /** The customer's names for the activities declaring it. */
  from: string[];
  /** Every role any declaration grants. Empty means every role reaches it. */
  reachedBy: string[];
  /** True when at least one declaration named no role, so every role reaches it. */
  everyRole: boolean;
}

function merge(activities: SpaceActivities): CanvasRow[] {
  const rows: CanvasRow[] = [];
  for (const one of activities.rows) {
    const entry = activities.entryFor(one);
    if (entry === undefined) continue;
    for (const canvas of canvasesOf(entry)) {
      const roles = canvas.roles ?? [];
      const held = rows.find((row) => row.kind === canvas.kind);
      if (held === undefined) {
        rows.push({
          ...canvas,
          from: [one.name],
          reachedBy: [...roles],
          everyRole: roles.length === 0,
        });
        continue;
      }
      if (!held.from.includes(one.name)) held.from.push(one.name);
      for (const role of roles) if (!held.reachedBy.includes(role)) held.reachedBy.push(role);
      if (roles.length === 0) held.everyRole = true;
      for (const alias of canvas.aliases ?? []) {
        if (!(held.aliases ?? []).includes(alias)) held.aliases = [...(held.aliases ?? []), alias];
      }
    }
  }
  return rows.sort((left, right) => left.kind.localeCompare(right.kind));
}

function Facts({ row }: { readonly row: CanvasRow }) {
  return (
    <div className="cx-chips">
      <Chip>{`scope ${row.scope ?? "singleton"}`}</Chip>
      <Chip>{`attach ${row.attach}`}</Chip>
      {row.required === true ? <Chip>required</Chip> : null}
      <Chip>{row.templated === false ? "fully scoped" : "templated"}</Chip>
      {row.on_machine_screen === true ? <Chip>on the machine screen</Chip> : null}
      {(row.aliases ?? []).map((alias) => (
        <Chip key={alias} tone="ns">{`also ${alias}`}</Chip>
      ))}
    </div>
  );
}

/** The devices paired to one kind, and the code that approves another. */
function Pairings({
  kind,
  pairings,
}: {
  readonly kind: string;
  readonly pairings: readonly CanvasPairing[];
}) {
  const unpair = useUnpairCanvas();
  const bound = pairings.filter((one) => one.canvas_kind === kind);

  if (bound.length === 0) return <Muted small>nothing paired</Muted>;

  return (
    <Stack gap={2}>
      {bound.map((one) => (
        <div className="cx-row" key={one.id}>
          <div className="cx-row-id">
            <div>
              <div className="cx-node-name">{one.label === "" ? one.canvas_kind : one.label}</div>
              <Muted small>{`last seen ${stamp(one.last_seen_at)}`}</Muted>
            </div>
          </div>
          {one.revoked_at === null ? (
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Unpair ${one.label === "" ? one.canvas_kind : one.label}`}
              loading={unpair.isPending}
              onClick={() => {
                unpair.mutate(one.id);
              }}
            >
              Unpair
            </Button>
          ) : (
            <StatusBadge tone="neutral">unpaired</StatusBadge>
          )}
        </div>
      ))}
    </Stack>
  );
}

export interface CanvasesPanelProps {
  readonly space: SpaceRow;
  readonly activities: SpaceActivities;
  readonly onOpenPanel: (id: PanelId) => void;
}

export function CanvasesPanel({ space, activities, onOpenPanel }: CanvasesPanelProps) {
  const joinCode = space.space.join_code;
  const pairings = useCanvasPairings();
  const approve = useApproveCanvasPairing();
  const [code, setCode] = useState("");

  const rows = merge(activities);

  if (activities.pending) {
    return <Nothing headline="Reading what is added" />;
  }

  if (rows.length === 0) {
    return (
      <Nothing
        headline="No added activity declares a canvas"
        action={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              onOpenPanel("catalog");
            }}
          >
            Open the catalog
          </Button>
        }
      />
    );
  }

  return (
    <Stack gap={5}>
      {rows.map((row) => {
        const addresses = canvasAddresses(row, joinCode);
        const first = addresses[0];
        return (
          <Section
            key={row.kind}
            title={row.kind}
            action={
              first === undefined ? undefined : (
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Open ${row.kind} in a new tab`}
                  onClick={() => {
                    window.open(first.value, "_blank", "noopener");
                  }}
                >
                  Open
                </Button>
              )
            }
          >
            <Stack gap={3}>
              <div className="cx-chips">
                <CanvasKindChip kind={row.kind} />
              </div>
              <Facts row={row} />

              <div className="cx-brings">
                <span className="cx-key">Declared by</span>
                <Muted small>{row.from.join(", ")}</Muted>
              </div>

              <div className="cx-brings">
                <span className="cx-key">Reached by</span>
                {row.everyRole ? (
                  <Muted small>every role</Muted>
                ) : (
                  <div className="cx-chips">
                    {row.reachedBy.map((role) => (
                      <Chip key={role}>{role}</Chip>
                    ))}
                  </div>
                )}
              </div>

              {addresses.length === 0 ? (
                <Muted small>the agent paints it on the machine screen</Muted>
              ) : (
                addresses.map((one) => (
                  <CopyField
                    key={one.value}
                    label={one.label}
                    value={one.value}
                  />
                ))
              )}

              <Loaded query={pairings} rows={1}>
                {(held) => <Pairings kind={row.kind} pairings={held} />}
              </Loaded>
            </Stack>
          </Section>
        );
      })}

      <Section title="Pair a device">
        <Stack gap={3}>
          <Field label="Code on the canvas">
            <Input
              value={code}
              mono
              placeholder="any case, any spacing"
              onChange={(event) => {
                setCode(event.target.value);
              }}
            />
          </Field>
          {approve.isError ? <StatusBadge tone="danger">{approve.error.message}</StatusBadge> : null}
          <Toolbar>
            <Button
              size="sm"
              variant="secondary"
              loading={approve.isPending}
              disabled={code.replace(/\s+/g, "") === ""}
              onClick={() => {
                approve.mutate(code.replace(/\s+/g, "").toUpperCase(), {
                  onSuccess: () => {
                    setCode("");
                  },
                });
              }}
            >
              Approve
            </Button>
          </Toolbar>
        </Stack>
      </Section>
    </Stack>
  );
}
